import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getRanking, type RankEntry } from "../lib/api";
import { fmt, fmt1 } from "../lib/format";

const MEDALS: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };
const PODIUM_ORDER = [2, 1, 3];
const HEIGHTS_PERSONAL = { 1: 70, 2: 50, 3: 36 } as const;
const HEIGHTS_DEPT     = { 1: 96, 2: 70, 3: 52 } as const;

function shortName(full: string): string {
  const parts = full.split(/\s+/);
  if (parts.length < 2) return full;
  return `${parts[0]} ${parts[1]![0]}.`;
}

function Podium({ sorted, myId, showNames, nameOf }: { sorted: RankEntry[]; myId: string; showNames: boolean; nameOf: (id: string) => string }) {
  const heights = showNames ? HEIGHTS_DEPT : HEIGHTS_PERSONAL;
  return (
    <div className={`flex items-end justify-center gap-2 ${showNames ? "h-36" : "h-24"} px-4 mt-1`}>
      {PODIUM_ORDER.map(place => {
        const r = sorted[place - 1];
        if (!r) return <div key={place} className="flex-1 max-w-[80px]" />;
        const isMe = !showNames && r.manager_id === myId;
        const medal = MEDALS[place];
        const stepBg = isMe ? "bg-violet-400" : "bg-slate-200";
        const textCol = isMe ? "text-white" : "text-slate-500";
        return (
          <div key={place} className="flex-1 flex flex-col items-center justify-end max-w-[80px]">
            <div className="text-xl mb-0.5">{medal}</div>
            <div className={`w-full ${stepBg} rounded-t-lg flex flex-col items-center justify-start pt-1`} style={{ height: `${heights[place as 1|2|3]}px` }}>
              <div className={`text-base font-bold ${textCol}`}>{place}</div>
            </div>
            {showNames && <div className="text-[9px] text-slate-600 mt-1 text-center leading-tight">{shortName(nameOf(r.manager_id))}</div>}
          </div>
        );
      })}
    </div>
  );
}

export function RatingScreen() {
  const { me, managers, period, periodId, managerId, t } = useAuth();
  const [entries, setEntries] = useState<RankEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!periodId) { setEntries([]); return; }
    let cancelled = false;
    setLoading(true);
    getRanking(period, periodId)
      .then(r => { if (!cancelled) setEntries(r.entries); })
      .catch(() => { if (!cancelled) setEntries([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, periodId]);

  const sortedBySales = [...entries].sort((a, b) => b.sales_per_day - a.sales_per_day);
  const sortedByReturns = [...entries].sort((a, b) => a.returns_pct - b.returns_pct);

  const isDept = managerId === "__dept__";
  // For non-admin, fall back to own manager_id from /api/me
  const myId = isDept ? "" : (managerId || me?.manager_id || "");

  const nameOf = (id: string): string => {
    if (managers.length > 0) {
      const m = managers.find(x => x.id === id);
      if (m) return m.full_name;
    }
    if (me?.manager_id === id && me?.name) return me.name;
    return id;
  };

  if (loading && entries.length === 0) {
    return <section className="p-4 text-sm text-slate-500">{t("err_loading")}</section>;
  }
  if (entries.length < 2) {
    return (
      <section className="p-4">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center text-sm text-amber-800">
          Рейтинг будет доступен, когда за выбранный период будут данные минимум по двум менеджерам.
        </div>
      </section>
    );
  }

  const salesPlace = sortedBySales.findIndex(r => r.manager_id === myId) + 1;
  const retPlace   = sortedByReturns.findIndex(r => r.manager_id === myId) + 1;
  const me_       = sortedBySales.find(r => r.manager_id === myId);
  const meReturns = sortedByReturns.find(r => r.manager_id === myId);

  return (
    <section className="p-4 space-y-3">
      <div className={`bg-white rounded-2xl border border-slate-200 ${isDept ? "p-3" : "p-2"}`}>
        <div className={`text-center ${isDept ? "mb-1" : ""}`}>
          {isDept ? (
            <>
              <div className="text-sm font-semibold text-slate-800">{t("rating_sales")}</div>
              <div className="text-[10px] text-slate-500">{t("rating_sales_full")}</div>
            </>
          ) : (
            <div className="text-sm font-semibold text-slate-800">
              {t("rating_sales")} <span className="text-[10px] text-slate-500 font-normal">{t("rating_sales_sub")}</span>
            </div>
          )}
        </div>
        <Podium sorted={sortedBySales} myId={myId} showNames={isDept} nameOf={nameOf} />
        {!isDept && me_ && (
          <div className="mt-1.5 bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-200 flex items-center justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold">{t("rating_you")}</span>
              <span className="text-sm font-bold text-slate-900 ml-1">{salesPlace} {t("rating_place_of")}</span>
              <span className="text-[10px] text-slate-500"> {t("rating_of")} {sortedBySales.length}</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-slate-900">{fmt(me_.sales_per_day)} <span className="text-[10px] font-normal text-slate-500">{t("rating_grn_per_day")}</span></div>
            </div>
          </div>
        )}
        {isDept && (
          <div className="mt-2 bg-slate-50 rounded-lg p-2 border border-slate-200 space-y-1">
            {sortedBySales.map((r, idx) => (
              <div key={r.manager_id} className="flex items-center justify-between text-xs">
                <span className="text-slate-700">{MEDALS[idx + 1] || ""} {nameOf(r.manager_id)}</span>
                <span className="font-semibold text-slate-900">{fmt(r.sales_per_day)} <span className="text-slate-500 font-normal">грн/день</span></span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`bg-white rounded-2xl border border-slate-200 ${isDept ? "p-3" : "p-2"}`}>
        <div className={`text-center ${isDept ? "mb-1" : ""}`}>
          {isDept ? (
            <>
              <div className="text-sm font-semibold text-slate-800">{t("rating_returns")}</div>
              <div className="text-[10px] text-slate-500">{t("rating_returns_full")}</div>
            </>
          ) : (
            <div className="text-sm font-semibold text-slate-800">
              {t("rating_returns")} <span className="text-[10px] text-slate-500 font-normal">{t("rating_returns_sub")}</span>
            </div>
          )}
        </div>
        <Podium sorted={sortedByReturns} myId={myId} showNames={isDept} nameOf={nameOf} />
        {!isDept && meReturns && (
          <div className="mt-1.5 bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-200 flex items-center justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold">{t("rating_you")}</span>
              <span className="text-sm font-bold text-slate-900 ml-1">{retPlace} {t("rating_place_of")}</span>
              <span className="text-[10px] text-slate-500"> {t("rating_of")} {sortedByReturns.length}</span>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-slate-900">{fmt1(meReturns.returns_pct)}<span className="text-[10px] font-normal text-slate-500">% от продаж</span></div>
            </div>
          </div>
        )}
        {isDept && (
          <div className="mt-2 bg-slate-50 rounded-lg p-2 border border-slate-200 space-y-1">
            {sortedByReturns.map((r, idx) => (
              <div key={r.manager_id} className="flex items-center justify-between text-xs">
                <span className="text-slate-700">{MEDALS[idx + 1] || ""} {nameOf(r.manager_id)}</span>
                <span className="font-semibold text-slate-900">{fmt1(r.returns_pct)} <span className="text-slate-500 font-normal">%</span></span>
              </div>
            ))}
          </div>
        )}
      </div>

      <details className="bg-white rounded-2xl border border-slate-200 px-3 py-1.5">
        <summary className="text-[11px] text-slate-600 cursor-pointer py-1 select-none">{t("rating_how")}</summary>
        <div className="text-[11px] text-slate-600 leading-relaxed pt-2 pb-1 space-y-1">
          <p>{t("rating_how_sales")}</p>
          <p>{t("rating_how_returns")}</p>
          <p className="text-slate-500">{t("rating_how_anon")}</p>
        </div>
      </details>
    </section>
  );
}
